import {ActionIcon, Avatar, Badge, Group, Paper, Stack, Text} from '@mantine/core'
import {IconMinus, IconPlus, IconTruck, IconUser} from '@tabler/icons-react'

import {
    actionDestructiveColor,
    identityEquipmentColor,
    identityWorkerColor,
} from 'src/theme/semantic-colors'

import {RentalAssetAssignmentPeek} from '../assets/rental-assets/rental-asset-assignment-peek'
import {WorkerAssignmentPeek} from '../assets/workers/worker-assignment-peek'
import type {FulfillmentGroupKind} from './assignment-grouping-utils'

type RentalAssetPeekProps = {
    assetId: string
    assetTypeName: string
    referenceDate: Date
}

type WorkerAssignmentPeekProps = {
    workerId: string
    workerTypeName: string
    referenceDate: Date
}

type ResourceAssignmentCardProps = {
    name: string
    kind: FulfillmentGroupKind
    isPlaceholder?: boolean
    variant: 'assigned' | 'available'
    /** Shown instead of the assign button when the resource cannot be assigned. */
    unavailableReason?: string
    disabled?: boolean
    rentalAssetPeek?: RentalAssetPeekProps
    workerAssignmentPeek?: WorkerAssignmentPeekProps
    onAssign?: () => void
    onRemove?: () => void
}

function ResourceAssignmentCardBody({
    name,
    kind,
    isPlaceholder = false,
    unavailableReason,
}: {
    name: string
    kind: FulfillmentGroupKind
    isPlaceholder?: boolean
    unavailableReason?: string
}) {
    const icon = kind === 'worker' ? <IconUser size={18} /> : <IconTruck size={18} />

    return (
        <Group gap="sm" wrap="nowrap" style={{minWidth: 0, flex: 1}}>
            <Avatar
                radius="xl"
                size="md"
                color={kind === 'worker' ? identityWorkerColor() : identityEquipmentColor()}
            >
                {icon}
            </Avatar>
            <Stack gap={0} style={{minWidth: 0}}>
                <Text size="sm" fw={500} truncate c={unavailableReason ? 'dimmed' : undefined}>
                    {name}
                </Text>
                {unavailableReason ? (
                    <Text size="xs" c="dimmed">
                        {unavailableReason}
                    </Text>
                ) : null}
            </Stack>
            {isPlaceholder ? (
                <Badge size="xs" variant="light" color="gray">
                    Placeholder
                </Badge>
            ) : null}
        </Group>
    )
}

export function ResourceAssignmentCard({
    name,
    kind,
    isPlaceholder,
    variant,
    unavailableReason,
    disabled = false,
    rentalAssetPeek,
    workerAssignmentPeek,
    onAssign,
    onRemove,
}: ResourceAssignmentCardProps) {
    const cardBody = (
        <ResourceAssignmentCardBody
            name={name}
            kind={kind}
            isPlaceholder={isPlaceholder}
            unavailableReason={unavailableReason}
        />
    )

    let wrappedBody = cardBody
    if (rentalAssetPeek) {
        wrappedBody = (
            <RentalAssetAssignmentPeek
                assetId={rentalAssetPeek.assetId}
                assetName={name}
                assetTypeName={rentalAssetPeek.assetTypeName}
                referenceDate={rentalAssetPeek.referenceDate}
            >
                {cardBody}
            </RentalAssetAssignmentPeek>
        )
    } else if (workerAssignmentPeek) {
        wrappedBody = (
            <WorkerAssignmentPeek
                workerId={workerAssignmentPeek.workerId}
                workerName={name}
                workerTypeName={workerAssignmentPeek.workerTypeName}
                referenceDate={workerAssignmentPeek.referenceDate}
            >
                {cardBody}
            </WorkerAssignmentPeek>
        )
    }

    return (
        <Paper withBorder p="sm" radius="md">
            <Group justify="space-between" wrap="nowrap" align="center">
                {wrappedBody}

                {variant === 'assigned' && onRemove && (
                    <ActionIcon
                        variant="subtle"
                        color={actionDestructiveColor()}
                        aria-label={`Remove ${name}`}
                        onClick={onRemove}
                    >
                        <IconMinus size={16} />
                    </ActionIcon>
                )}

                {variant === 'available' && onAssign && !unavailableReason && (
                    <ActionIcon
                        variant="light"
                        aria-label={`Assign ${name}`}
                        disabled={disabled}
                        onClick={onAssign}
                    >
                        <IconPlus size={16} />
                    </ActionIcon>
                )}
            </Group>
        </Paper>
    )
}
